import { takeEvery, put, call } from 'redux-saga/effects';
import { appUserAuthTypes, fetchTenants, requestTenantRedirect } from '../actions/appUserAuth';
import { formatMessage } from '../i18n/language';
import CoreApi from '../services/coreApi';

// Friendly, on-brand messages shown in the login's own alert banner
// (see UnifiedLogin/LoginAlert) - never the raw "Failed to fetch". A thrown
// fetch means the Core is unreachable (server down / no network); a non-ok
// response means the server answered with an error. Resolved lazily (inside
// each saga) so the current language is read at display time.
const connectionMessage = () => formatMessage({ id: 'login.error.connection' });
const serverMessage = () => formatMessage({ id: 'login.error.server' });

const errorAlert = message => ({
  type: appUserAuthTypes.SET_ALERT,
  alert: { kind: 'error', message }
});

// REQUEST OTP
function* requestOtpSaga(action) {
  try {
    const response = yield call(CoreApi.requestOtp, action.email);
    if (response.ok) {
      yield put({ type: appUserAuthTypes.REQUEST_OTP_SUCCESS });
    } else {
      yield put({ type: appUserAuthTypes.REQUEST_OTP_FAILURE });
      yield put(errorAlert(serverMessage()));
    }
  } catch (e) {
    yield put({ type: appUserAuthTypes.REQUEST_OTP_FAILURE });
    yield put(errorAlert(connectionMessage()));
  }
}

// VERIFY OTP - OTP-specific failures (wrong/expired code) render inline in the
// OTP step; a connection failure surfaces in the alert banner instead (not as
// a misleading "código incorrecto"). The Core returns
// { error: 'invalid_credentials' | 'no_tenants' | ... } on 401.
function* verifyOtpSaga(action) {
  try {
    const response = yield call(CoreApi.verifyOtp, action.email, action.otp);

    if (response.ok) {
      const authorization = response.headers.get('Authorization');
      localStorage.setItem('appUserJwt', authorization ? authorization.split('Bearer ')[1] : '');
      yield put({ type: appUserAuthTypes.VERIFY_OTP_SUCCESS });
      yield put(fetchTenants());
    } else {
      let reason = 'invalid';
      try {
        const data = yield response.json();
        reason = data.error || reason;
      } catch (e) {
        // non-JSON body - keep the generic reason
      }
      yield put({ type: appUserAuthTypes.VERIFY_OTP_FAILURE, reason });
    }
  } catch (e) {
    // Connection failure: no inline OTP error, show the banner.
    yield put({ type: appUserAuthTypes.VERIFY_OTP_FAILURE, reason: null });
    yield put(errorAlert(connectionMessage()));
  }
}

// FETCH TENANTS
function* fetchTenantsSaga() {
  try {
    const response = yield call(CoreApi.fetchTenants);
    if (!response.ok) {
      yield put({ type: appUserAuthTypes.FETCH_TENANTS_FAILURE });
      yield put(errorAlert(serverMessage()));
      return;
    }

    const data = yield response.json();
    const tenants = data.tenants || [];
    // Support mailboxes get { support: true, tenants: [] } - the client shows a
    // "type the subdomain" flow instead of a picker, so no auto-select.
    yield put({ type: appUserAuthTypes.FETCH_TENANTS_SUCCESS, tenants, support: data.support });
    if (data.support) return;

    // Auto-select when there is exactly one tenant. Payment-suspended ones
    // count too: they are enterable and the in-tenant service-suspended page is
    // the gate (mirrors Identity::LoginsController#verify in the Core).
    if (tenants.length === 1) {
      yield put(requestTenantRedirect(tenants[0].subdomain));
    }
  } catch (e) {
    yield put({ type: appUserAuthTypes.FETCH_TENANTS_FAILURE });
    yield put(errorAlert(connectionMessage()));
  }
}

// REQUEST TENANT REDIRECT
function* requestTenantRedirectSaga(action) {
  try {
    const response = yield call(CoreApi.requestTenantRedirect, action.subdomain);
    if (!response.ok) {
      yield put({ type: appUserAuthTypes.REQUEST_TENANT_REDIRECT_FAILURE });
      yield put(errorAlert(formatMessage({ id: 'login.error.tenantRedirect' })));
      return;
    }

    const data = yield response.json();
    yield put({ type: appUserAuthTypes.REQUEST_TENANT_REDIRECT_SUCCESS });
    // Full top-level navigation, NOT a fetch/XHR: the target subdomain's
    // Devise session cookie is host-only and is set by the Set-Cookie header
    // on /tenant_switch/consume's response. A fetch (even with
    // credentials: 'include') would not let the browser apply that cookie
    // against the target subdomain the way a real navigation does - do not
    // "optimize" this into an API call.
    window.location.href = data.redirect_url;
  } catch (e) {
    yield put({ type: appUserAuthTypes.REQUEST_TENANT_REDIRECT_FAILURE });
    yield put(errorAlert(connectionMessage()));
  }
}

export default function* appUserAuthSagas() {
  yield takeEvery(appUserAuthTypes.REQUEST_OTP_REQUEST, requestOtpSaga);
  yield takeEvery(appUserAuthTypes.VERIFY_OTP_REQUEST, verifyOtpSaga);
  yield takeEvery(appUserAuthTypes.FETCH_TENANTS_REQUEST, fetchTenantsSaga);
  yield takeEvery(appUserAuthTypes.REQUEST_TENANT_REDIRECT_REQUEST, requestTenantRedirectSaga);
}
